/**
 * Annexure Column Definitions and Validation Schema for New TDS Return Form 144 (replaces 27Q)
 * Applicable from FY 2026-27 onwards.
 *
 * Includes all Form 140 changes PLUS 27Q-specific fields:
 * - "Deductee Code" → "Status" (dropdown with Note-1 values)
 * - "Country of residence of the deductee" → "Country to which remittance is made"
 * - "Grossing up indicator" → "Whether the amount paid or credited is grossed up as per section 393(10) (Yes/No)"
 * - "Unique acknowledgement of form no 15CA" → "...Form No. 145, if applicable"
 * - Certificate field → section 395(1)/395(2) reference
 * - Removed: Paid by Book Entry, old sub-clause cash withdrawal fields
 */

import { z } from 'zod'
import {
  mandatoryNumeric12_2,
  ddMMYYYYWithSlash,
  ddMMYYYYWithSlashOptional,
  isZeroOrEmptyNumeric,
} from './validationHelpers'
import {
  COUNTRY_LIST,
  GROSSING_UP_OPTIONS,
  NATURE_OF_REMITTANCE_OPTIONS,
  TDS_RATE_BELONGS_TO_OPTIONS,
} from '.'

/**
 * Cross-field rule shared by Form 144 and the 27Q new-form path:
 * "Date of deduction" is required only when "Total tax deducted" is a
 * non-zero value. When it is zero/undefined/null, the date stays optional.
 */
const refineDateOfDeductionByTaxDeducted = (data, ctx) => {
  if (!isZeroOrEmptyNumeric(data.totalTaxDeducted) && !data.dateOfDeduction) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['dateOfDeduction'],
      message: 'Date of Deduction is required.',
    })
  }
}

// Status dropdown options (was "Deductee Code" in 27Q)
// Values from Note-1 of the new form specification
// NOTE: Retained for the legacy 27Q new-form path (see RETURNS_ANNEXURE_COLUMNS_27Q_NEW).
// Form 144 now uses DEDUCTEE_STATUS_OPTIONS_144 (Vendor Type -> Code mapping) per PNW-1262.
export const DEDUCTEE_STATUS_OPTIONS = [
  { id: 1, label: 'Company, other than domestic company', value: '1' },
  { id: 2, label: 'Individual', value: '2' },
  { id: 3, label: 'Hindu undivided family', value: '3' },
  { id: 4, label: 'Association of Persons (AOP) except in case of AOP consisting of only companies as its members', value: '4' },
  { id: 5, label: 'Association of Persons (AOP) consisting of only companies as its members', value: '5' },
  { id: 6, label: 'Co-operative society', value: '6' },
  { id: 7, label: 'Firm', value: '7' },
  { id: 8, label: 'Body of individuals', value: '8' },
  { id: 9, label: 'Artificial juridical person', value: '9' },
  { id: 10, label: 'Others', value: '10' },
]

/**
 * Form 144 "Status" dropdown options — PNW-1262.
 * Mapped from the Vendor Master "Vendor Type" -> Code mapping (Regular File).
 * One option per unique code; NA/Others, Trust, Government Authority and
 * Local Authority all map to code '10'.
 * The backend supplies the mapped code on Form 144 rows; this list is the
 * set of selectable options for editing "Status" from the UI.
 */
export const DEDUCTEE_STATUS_OPTIONS_144 = [
  { id: 1, label: 'Company', value: 'Company' },
  { id: 2, label: 'Individual', value: 'Individual' },
  { id: 3, label: 'Hindu Undivided Family (HUF)', value: 'Hindu Undivided Family (HUF)' },
  { id: 5, label: 'AOP', value: 'AOP' },
  { id: 7, label: 'Firm', value: 'Firm' },
  { id: 8, label: 'Body of Individual', value: 'Body of Individual' },
  { id: 9, label: 'Artificial Juridical Person', value: 'Artificial Juridical Person' },
  { id: 10, label: 'Others', value: 'Others' },
  { id: 11, label: 'Trust', value: 'Trust' },
  { id: 12, label: 'Government Authority', value: 'Government Authority' },
  { id: 13, label: 'Local Authority', value: 'Local Authority' },
]

// Valid Status codes for Form 144 (PNW-1262) — derived from DEDUCTEE_STATUS_OPTIONS_144.
const DEDUCTEE_STATUS_CODES_144 = DEDUCTEE_STATUS_OPTIONS_144.map(o => o.value)

/**
 * Form 144 "Nature of remittance" dropdown options — PNW-1262.
 * `id` holds the 2-digit code (01–19); `value`/`label` hold the description.
 * Per sign-off, the description (not the code) is populated into the VTD/return.
 */
export const NATURE_OF_REMITTANCE_OPTIONS_144 = [
  { id: '01', value: 'WINNINGS FROM LOTTERY OR CROSS WORD PUZZLE, CARD GAME, GAMBLING OR BETTING OF ANY FORM ETC', label: 'WINNINGS FROM LOTTERY OR CROSS WORD PUZZLE, CARD GAME, GAMBLING OR BETTING OF ANY FORM ETC' },
  { id: '02', value: 'WINNINGS FROM ONLINE GAMES', label: 'WINNINGS FROM ONLINE GAMES' },
  { id: '03', value: 'WINNINGS FROM HORSE RACE', label: 'WINNINGS FROM HORSE RACE' },
  { id: '04', value: 'PAYMENTS TO NON-RESIDENT SPORTSMEN OR ENTERTAINER/SPORT ASSOCIATIONS OR INSTITUTION', label: 'PAYMENTS TO NON-RESIDENT SPORTSMEN OR ENTERTAINER/SPORT ASSOCIATIONS OR INSTITUTION' },
  { id: '05', value: 'INTEREST INCOME', label: 'INTEREST INCOME' },
  { id: '06', value: 'DIVIDEND INCOME REFERRED TO IN THE IN THE SECTION 207(1) [Table: Sl. No.1]', label: 'DIVIDEND INCOME REFERRED TO IN THE IN THE SECTION 207(1) [Table: Sl. No.1]' },
  { id: '07', value: 'DIVIDEND INCOME REFERRED TO IN THE SECTION 207(1) [Table: Sl. No.2]', label: 'DIVIDEND INCOME REFERRED TO IN THE SECTION 207(1) [Table: Sl. No.2]' },
  { id: '08', value: 'INCOME BY WAY OF RENTING OR LEASING OR LETTING OUT ANY REAL ESTATE ASSET', label: 'INCOME BY WAY OF RENTING OR LEASING OR LETTING OUT ANY REAL ESTATE ASSET' },
  { id: '09', value: 'INVESTMENT INCOME', label: 'INVESTMENT INCOME' },
  { id: '10', value: 'LONG TERM CAPITAL GAINS REFERRED TO IN SECTION 214 [Table: Sl. No. 2] or 197(1)', label: 'LONG TERM CAPITAL GAINS REFERRED TO IN SECTION 214 [Table: Sl. No. 2] or 197(1)' },
  { id: '11', value: 'LONG TERM CAPITAL GAINS REFERRED TO IN SECTION 198 EXCEEDING ONE LAKH TWENTY-FIVE THOUSAND RUPEES', label: 'LONG TERM CAPITAL GAINS REFERRED TO IN SECTION 198 EXCEEDING ONE LAKH TWENTY-FIVE THOUSAND RUPEES' },
  { id: '12', value: 'LONG TERM CAPITAL GAINS REFERRED TO IN SECTION 214 [Table: Sl. No. 1]', label: 'LONG TERM CAPITAL GAINS REFERRED TO IN SECTION 214 [Table: Sl. No. 1]' },
  { id: '13', value: 'SHORT TERM CAPITAL GAINS REFERRED TO IN SECTION 196', label: 'SHORT TERM CAPITAL GAINS REFERRED TO IN SECTION 196' },
  { id: '14', value: 'SHORT TERM CAPITAL GAINS (NOT BEING SHORT TERM CAPITAL GAINS REFERRED TO IN SECTION 196)', label: 'SHORT TERM CAPITAL GAINS (NOT BEING SHORT TERM CAPITAL GAINS REFERRED TO IN SECTION 196)' },
  { id: '15', value: 'COMMISSION', label: 'COMMISSION' },
  { id: '16', value: 'FEE FOR TECHNICAL SERVICES/ INCLUDED SERVICES', label: 'FEE FOR TECHNICAL SERVICES/ INCLUDED SERVICES' },
  { id: '17', value: 'ROYALTY', label: 'ROYALTY' },
  { id: '18', value: 'CASH WITHDRAWAL', label: 'CASH WITHDRAWAL' },
  { id: '19', value: 'OTHER INCOME', label: 'OTHER INCOME' },
]

/**
 * Zod validation schema for Annexure rows in new Form 144.
 *
 * Differences from legacy returnsAnnexureRowSchema:
 * - Removed: tds, surcharge, healthAndEducationCess (individual fields gone)
 * - Removed: deducteeReferenceNo (field no longer exists)
 * - Renamed: deducteeCode is now "Status" but field ID remains deducteeCode
 * - Added: totalTaxDeducted as mandatory (replaces individual breakdown)
 * - Retained: natureOfRemittance (mandatory for 144)
 * - Retained: vendorEmailId, vendorContactNumber with same validation as legacy 27Q
 * - Added: acknowledgement15CA (Form No. 145 reference, optional)
 */
// Base object (unrefined) so it can be extended for the 27Q new-form path.
// The exported schemas below attach the conditional dateOfDeduction rule.
const returnsAnnexureRowSchema144Base = z.object({
  challanSerialNo: z
    .string()
    .nonempty({ message: 'Challan Serial No. is required.' })
    .regex(/^\d{1,9}$/, {
      message: 'Must be numeric and up to 9 digits.',
    }),

  sectionCode: z.string().nonempty({ message: 'Section Code is required.' }),

  panOfDeductee: z
    .string()
    .min(10, 'PAN is required')
    .max(10)
    .regex(
      /^[A-Z]{3}[ABCFGHLJPT]{1}[A-Z]{1}[0-9]{4}[A-Z]{1}$/i,
      'Invalid PAN format'
    ),

  deducteeName: z
    .string()
    .nonempty({ message: 'Name is required.' })
    .regex(/^[^^\u005E]*$/, 'Invalid characters (caret ^ not allowed)'),

  dateOfPaymentOrCredit: ddMMYYYYWithSlash,

  amountPaidOrCredited: mandatoryNumeric12_2,
  totalTaxDeposited: mandatoryNumeric12_2,
  totalTaxDeducted: mandatoryNumeric12_2,

  // Conditionally required: enforced via superRefine based on totalTaxDeducted.
  dateOfDeduction: ddMMYYYYWithSlashOptional,

  rateOfTaxDeducted: z.string().regex(/^\d{1,3}(\.\d{1,2})?$/, {
    message: 'Must be a number up to 3 digits and 2 decimals',
  }),

  deducteeCode: z
    .union([z.string(), z.number()])
    .transform(val => String(val))
    .pipe(
      z.string()
        .nonempty({ message: 'Status is required.' })
        .refine(
          val => DEDUCTEE_STATUS_CODES_144.includes(val),
          { message: 'Invalid Status value. Select from the Vendor Type mapping.' }
        )
    ),

  natureOfRemittance: z
    .string()
    .nonempty({ message: 'Nature of Remittance is required for Form 144.' }),

  vendorEmailId: z
    .string()
    .trim()
    .transform(val => (val === '' ? undefined : val))
    .optional()
    .nullable()
    .refine(val => !val || z.string().email().safeParse(val).success, {
      message: 'Invalid Email format',
    }),

  vendorContactNumber: z
    .string()
    .trim()
    .transform(val => (val === '' ? undefined : val))
    .optional()
    .nullable()
    .refine(val => !val || /^[0-9]{10,15}$/.test(val), {
      message: 'Contact number must be 10–15 digits',
    }),

  certificateNo197: z
    .string()
    .regex(/^[^^\u005E]*$/, 'Invalid characters (caret ^ not allowed)')
    .nullable()
    .optional(),

  remarks: z
    .string()
    .optional()
    .nullable(),

  acknowledgement15CA: z
    .string()
    .nullable()
    .optional(),

  countryOfResidence: z
    .string()
    .nullable()
    .optional(),

  grossingUpIndicator: z
    .string()
    .nullable()
    .optional(),

  optingOutTaxRegime: z
    .string()
    .nullable()
    .optional(),
})

// Exported Form 144 schema: base object + conditional dateOfDeduction rule.
export const returnsAnnexureRowSchema144 =
  returnsAnnexureRowSchema144Base.superRefine(
    refineDateOfDeductionByTaxDeducted
  )

export const RETURNS_ANNEXURE_COLUMNS_144 = [
  {
    id: 'serialNo',
    label: 'Row number',
    width: '150px',
  },
  {
    id: 'challanSerialNo',
    label: 'Challan serial no.',
    width: '150px',
    editable: true,
    isFormatted: false,
  },
  {
    id: 'updateMode',
    label: 'Update mode for deductee (Add/ Update/ PAN Update)',
    width: '250px',
    align: 'center',
  },
  {
    id: 'bsrCode',
    label: 'BSR code of branch where tax deposited',
    width: '250px',
    align: 'center',
    isFormatted: false,
  },
  {
    id: 'dateOfTaxDeposited',
    label: 'Date on which tax deposited (DD/MM/YYYY)',
    width: '250px',
    align: 'center',
  },
  {
    id: 'transferVoucherOrChallanSerialNo',
    label: 'Transfer voucher/ challan serial no.',
    width: '200px',
    align: 'center',
    isFormatted: false,
  },
  {
    id: 'totalTdsToBeAllocated',
    label: 'Total',
    width: '180px',
    align: 'left',
    tooltip: '(Total Tax Deposited - Others) from allocate page',
  },
  {
    id: 'deducteeSerialNo',
    label: 'S. no.',
    width: '150px',
  },
  {
    id: 'lastPanOfDeductee',
    label: 'Last PAN of deductee',
    width: '200px',
  },
  {
    id: 'panOfDeductee',
    label: 'PAN of the deductee',
    width: '200px',
    editable: true,
  },
  {
    id: 'deducteeName',
    label: 'Name',
    width: '240px',
    editable: true,
  },
  {
    id: 'vendorTaxIdentificationNo',
    label:
      'Tax Identification Number/ Unique identification number issued by the country of residence',
    width: '300px',
    align: 'center',
    editable: row => row.formType === '144' && row.tdsRateActOrDtaa !== 'A',
  },
  {
    id: 'vendorAddress',
    label: 'Address in country of residence',
    width: '250px',
    align: 'center',
    editable: row => row.formType === '144' && row.tdsRateActOrDtaa !== 'A',
  },
  {
    id: 'vendorEmailId',
    label: 'Email',
    width: '200px',
    align: 'left',
    editable: row => row.formType === '144' && row.tdsRateActOrDtaa !== 'A',
  },
  {
    id: 'vendorContactNumber',
    label: 'Contact Number',
    width: '200px',
    align: 'left',
    editable: row => row.formType === '144' && row.tdsRateActOrDtaa !== 'A',
    isFormatted: false,
  },
  {
    id: 'deducteeCode',
    label: 'Status',
    width: '300px',
    editable: true,
    type: 'dropdown',
    options: DEDUCTEE_STATUS_OPTIONS_144,
  },
  {
    id: 'sectionCode',
    label: 'Section code',
    width: '200px',
    align: 'left',
    editable: true,
    isFormatted:false,
    type: 'dropdown',
    options: [], // Populated dynamically from NEW_SECTION_CODE API
  },
  {
    id: 'natureOfRemittance',
    label: 'Nature of remittance',
    width: '250px',
    align: 'left',
    editable: true,
    type: 'dropdown',
    options: NATURE_OF_REMITTANCE_OPTIONS_144,
  },
  {
    id: 'countryOfResidence',
    label: 'Country to which remittance is made',
    width: '250px',
    align: 'center',
    editable: true,
    type: 'dropdown',
    options: COUNTRY_LIST,
  },
  {
    id: 'optingOutTaxRegime',
    label: 'Whether deductee opting out of taxation regime u/s 202(1)?',
    width: '300px',
    align: 'center',
    editable: true,
    type: 'dropdown',
    options: GROSSING_UP_OPTIONS,
  },
  {
    id: 'dateOfPaymentOrCredit',
    label: 'Date of payment/ credit (DD/MM/YYYY)',
    width: '250px',
    align: 'center',
    editable: true,
  },
  {
    id: 'amountPaidOrCredited',
    label: 'Amount paid or credited',
    width: '200px',
    align: 'center',
    editable: true,
  },
  {
    id: 'grossingUpIndicator',
    label:
      'Whether the amount paid or credited is grossed up as per section 393(10) (Yes/No)',
    width: '350px',
    align: 'center',
    editable: true,
    type: 'dropdown',
    options: GROSSING_UP_OPTIONS,
  },
  {
    id: 'cashWithdrawalExceeding1Cr',
    label:
      'Amount of cash withdrawal in excess of Rs. 1 crore as referred to in section 393(3) [Table: Sl. No. 5] (in case of person other than cooperative society)',
    width: '300px',
    align: 'center',
  },
  {
    id: 'cashWithdrawalExceeding3CrCooperative',
    label:
      'Amount of cash withdrawal which is in excess of Rs. 3 Crore for cases covered by section 393(3) [Table: Sl. No. 5] (in case of cooperative societies)',
    width: '300px',
    align: 'center',
  },
  {
    id: 'totalTaxDeducted',
    label: 'Total tax deducted',
    width: '160px',
    align: 'center',
  },
  {
    id: 'lastTotalTaxDeducted',
    label: 'Last total tax deducted',
    width: '180px',
    align: 'center',
  },
  {
    id: 'totalTaxDeposited',
    label: 'Total tax deposited',
    width: '160px',
    align: 'left',
    editable: true,
  },
  {
    id: 'lastTotalTaxDeposited',
    label: 'Last total tax deposited',
    width: '240px',
  },
  {
    id: 'dateOfDeduction',
    label: 'Date of deduction (DD/MM/YYYY)',
    width: '240px',
    align: 'left',
    editable: true,
  },
  {
    id: 'rateOfTaxDeducted',
    label: 'Rate at which tax deducted',
    width: '160px',
    editable: true,
  },
  {
    id: 'tdsRateActOrDtaa',
    label: 'Whether TDS rate of TDS is IT act (a) and DTAA (b)',
    width: '250px',
    align: 'center',
    editable: true,
    type: 'dropdown',
    options: TDS_RATE_BELONGS_TO_OPTIONS,
  },
  {
    id: 'acknowledgement15CA',
    label:
      'Unique acknowledgement number of the corresponding Form No. 145, if applicable',
    width: '300px',
    align: 'center',
    editable: true,
  },
  {
    id: 'remarks',
    label:
      'Reason for non-deduction/ lower deduction/ higher deduction (refer Notes 1 to 9)',
    width: '350px',
    editable: true,
  },
  {
    id: 'certificateNo197',
    label:
      'Certificate number(s) of the certificate under section 395(1)/ 395(2) issued for non-deduction/ lower deduction',
    width: '350px',
    align: 'center',
    editable: true,
  },
]

/**
 * Preserved 27Q new-form definitions — PNW-1262.
 *
 * Prior to PNW-1262 the 27Q new-form path shared RETURNS_ANNEXURE_COLUMNS_144
 * and returnsAnnexureRowSchema144. Since PNW-1262 updates apply to Form 144 ONLY,
 * these preserve the previous behaviour for 27Q by keeping the original
 * NATURE_OF_REMITTANCE_OPTIONS and DEDUCTEE_STATUS_OPTIONS (Note-1) lists.
 */
export const RETURNS_ANNEXURE_COLUMNS_27Q_NEW = RETURNS_ANNEXURE_COLUMNS_144.map(col => {
  if (col.id === 'deducteeCode') {
    return { ...col, options: DEDUCTEE_STATUS_OPTIONS }
  }
  if (col.id === 'natureOfRemittance') {
    return { ...col, options: NATURE_OF_REMITTANCE_OPTIONS }
  }
  return col
})

// Valid Status codes for the 27Q new-form path (Note-1 values, unchanged).
const DEDUCTEE_STATUS_CODES_27Q = DEDUCTEE_STATUS_OPTIONS.map(o => o.value)

export const returnsAnnexureRowSchema27QNew = returnsAnnexureRowSchema144Base
  .extend({
    deducteeCode: z
      .union([z.string(), z.number()])
      .transform(val => String(val))
      .pipe(
        z.string()
          .nonempty({ message: 'Status is required.' })
          .refine(
            val => DEDUCTEE_STATUS_CODES_27Q.includes(val),
            { message: 'Invalid Status value. Select from Note-1 options.' }
          )
      ),
  })
  .superRefine(refineDateOfDeductionByTaxDeducted)
